import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LPCO_INFO_U, SG_LPCO_CPNT_LIST_U } from "./services/OsciCfgnCpntService";
import { CommonErrors } from "@/comn/components";
import { lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Register LPCO Component
 * !== LPCO Component 등록 ==!
 */
export const OSCI0203004S = () => {
    const { getResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0116" },
            { area: "comnCd", comnCd: "OSC0100" },
            { area: "comnCd", comnCd: "OSC0125" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0114" },
            { area: "comnCd", comnCd: "OSC0025" },
            { area: "comnCd", comnCd: "OSC0126" },
        ],
    });
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { appFmId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [lpcoCpntList, setLpcoCpntList] = useState(comnUtils.getGridData([]));

    // original data
    const orginDataRef = useRef({ info: {}, cpntList: [] });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_U,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Component List !== Component 목록 ==!
        lpcoCpntList: useGrid({
            defaultSchema: SG_LPCO_CPNT_LIST_U,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_LpcoCnptList: {
            head: {},
            cell: {},
            // Lock the saved General component row against deletion !== 저장된 General 컴포넌트 행은 삭제 차단 ==!
            checkbox: (row: any) => !(row.cpntCd === "GEN" && row.cpntId),
            edit: {
                cpntCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <FormControl
                            type="select"
                            comnCd="OSC0116"
                            area="comnCd"
                            excludes={["DCL", "ATC", "PAY", "MSG", "CON"]}
                            value={rowValues.cpntCd}
                            onChange={async (v: any) => {
                                grid.lpcoCpntList.updateRow({
                                    ...rowValues,
                                    cpntCd: v,
                                    cpntCdNm: v
                                        ? await lpcoUtils.getCodeLabel(getResource("comnCd", "OSC0116"), v)
                                        : "",
                                });
                            }}
                        />
                    );
                },
            },
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //return (await APIS.getLpcoCnptDtl(appFmId)).data?.appInfo.content || [];
        /* Register LPCO information !== LPCO 정보 등록 ==! */
        saveAppCpnt: useFetch({
            api: (data) => APIS.saveAppCpnt(appFmId, { ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203003s + "/" + data.appInfo?.content?.appFmId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203003s + "/" + data.appInfo?.content?.appFmId);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.lpcoInfoRgsr.setErrors(error.response.data.errors);
                }

                const errMsg = error?.response?.data?.errorMessage || "msg.osc.00015";
                modal.openModal({
                    content: errMsg,
                });
            },
        }),

        getAppDtl: useFetch({
            api: async (data) => await APIS.getAppDtl(appFmId),
            enabled: !!appFmId,
            onSuccess: (data) => {
                if (!data.appInfo.content.info || !data.appInfo.content.info?.appFmId) {
                    // no data
                    modal.openModal({
                        content: "msg.osc.00050",
                        onCancel: () => {
                            navigate(URLS.osci0203002q);
                        },
                    });
                } else {
                    form.lpcoInfoRgsr.setValues({ ...data.appInfo?.content.info } || {});
                    grid.lpcoCpntList.setData(comnUtils.getGridData([...data.appInfo?.content.cpntList]));

                    console.log("data", data);

                    orginDataRef.current.info = { ...data.appInfo?.content.info } || {};
                    orginDataRef.current.cpntList = Array.from(data.appInfo.content?.cpntList) || [];
                }
            },
            onError: (error) => {},
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    const handler = {
        // Save Component !== LPCO Component 저장 ==!
        saveAppCpnt: form.lpcoInfoRgsr.handleSubmit(() => {
            // check duplicate
            const lpcoCpntList = grid.lpcoCpntList.getViewData();

            if (!lpcoCpntList || lpcoCpntList.length == 0) {
                // Enter component information. !== 컴포넌트를 입력하세요.==!
                modal.openModal({
                    content: "msg.osc.00004",
                });
            } else {
                // Duplicate check !== 중복 체크 ==!
                // grid validate
                let errResult = grid.lpcoCpntList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                const duplMap: { [key: string]: boolean } = {};
                let idx = 1;
                for (const item of lpcoCpntList) {
                    const val = item.cpntCd;
                    if (val && duplMap[val]) {
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_CPNT_NM", "msg.osc.00017", item.cpntCdNm));
                    } else {
                        duplMap[val] = true;
                    }
                    idx++;
                }
                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else if (!lpcoCpntList.some((item: any) => item.cpntCd === "GEN")) {
                    // The General component is required. !== General 컴포넌트는 필수입니다. ==!
                    modal.openModal({
                        content: "msg.osc.00306",
                    });
                } else if (
                    lpcoCpntList.some((item: any) => item.cpntCd === "ITM") &&
                    !lpcoCpntList.some((item: any) => item.cpntCd === "INV")
                ) {
                    // The Invoice component is required when the Item component is used. !== Item 사용 시 Invoice 필수 ==!
                    modal.openModal({
                        content: "msg.osc.00307",
                    });
                } else if (form.lpcoInfoRgsr.getValues("aprvNoPtnCd") === "009" && !form.lpcoInfoRgsr.getValues("aprvNoPtnValue")) {
                    // Pattern 009 needs a pattern value !== 009 형식은 패턴값이 필요하다 ==!
                    modal.openModal({
                        content: t("msg.osc.00051", { 0: t("L_LPCO_APRV_NO_PTN_VALUE") }),
                    });
                } else if (
                    form.lpcoInfoRgsr.getValues("aprvNoPtnCd") === "009" &&
                    (form.lpcoInfoRgsr.getValues("aprvNoPtnValue").match(/#+/g) || []).length !== 1
                ) {
                    // Pattern value needs exactly one run of '#' !== 패턴값에는 # 묶음이 정확히 하나 있어야 한다 ==!
                    modal.openModal({
                        content: t("msg.osc.00338", { 0: t("L_LPCO_APRV_NO_PTN_VALUE") }),
                    });
                } else {
                    const data = { ...form.lpcoInfoRgsr.getValues(), cpntList: grid.lpcoCpntList.getData() };
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            // Save func
                            fetch.saveAppCpnt.fetch(data);
                        },
                    });
                }
            }
        }),
        click_Btn_AddGridCpntList: () => {
            grid.lpcoCpntList.addRow();
        },
        click_Btn_DeleteGridCpntList: () => {
            const seltList: any[] = grid.lpcoCpntList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.lpcoCpntList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.lpcoAtchFileList.deleteRow("checkbox");
            //     },
            // });
        },
        setAppDtl: async (appFmId: any, bInit: boolean) => {
            fetch.getAppDtl.fetch(appFmId);
        },
    };

    useEffect(() => {
        handler.setAppDtl(appFmId, true); // lpco app info
    }, []);

    const daysUtCd = form.lpcoInfoRgsr.watch("daysUtCd");
    const aprvNoPtnCd = form.lpcoInfoRgsr.watch("aprvNoPtnCd");

    return (
        <Page
        // id={pgeUid}
        // title={t("L_EDIT_FM_FLD")}
        // navigation={{
        //     base: comnEnvs.base,
        //     nodes: [
        //         ...BASE.nodes,
        //         { path: "/eswo/lpco/cfgn/cpnt/osci0203002q", label: "T_CPNT_FM_LST_SRCH" },
        //         { path: "/eswo/lpco/cfgn/cpnt/osci0203004s" + "/" + appFmId, label: "L_EDIT_FM_FLD" },
        //     ],
        // }}
        >
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.orgNm}></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appCdNo}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appNm}></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appKndCd}></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.atcmTpCd} controlSize={4}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Label label={"L_VLD_PRID"} required={true} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl
                                        {...form.lpcoInfoRgsr.schema.daysUtCd}
                                        onChange={(v: any) => {
                                            // End of Year fixes the period to 1 !== 연말(E)은 기간 1 고정 ==!
                                            if (v === "E") form.lpcoInfoRgsr.setValue("vldPridDays", 1);
                                        }}
                                    />
                                    <FormControl {...form.lpcoInfoRgsr.schema.vldPridDays} readOnly={daysUtCd === "E"} />
                                </Layout>
                            </Group.Col>
                            <Group.Control {...form.lpcoInfoRgsr.schema.aprvNoPtnCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.useYn}></Group.Control>
                            {/* Pattern value only for pattern 009 !== 패턴값은 009일 때만 ==! */}
                            {aprvNoPtnCd === "009" && <Group.Control {...form.lpcoInfoRgsr.schema.aprvNoPtnValue}></Group.Control>}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appImexTpCd}></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.intfYn}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_CPNT"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_AddGridCpntList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridCpntList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid {...grid.lpcoCpntList.grid} data={lpcoCpntList} render={render.grid_LpcoCnptList} />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203002q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.lpcoInfoRgsr.reset({ ...orginDataRef.current.info });
                            grid.lpcoCpntList.resetData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveAppCpnt}></Button>
                    <Button role="cancel" onClick={() => navigate(`${URLS.osci0203003s}/${appFmId}`)}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
